import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Stack, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid-premium';
import currency from 'currency.js';
import { format } from 'date-fns';
import { get } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { lineItemFormText } from '../../constants/lineItems';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import { getAllocationShortfallMessage, getAllocationShortfalls } from '../../util/allocationWarnings';
import { formatServerDate, removeCommas } from '../../util/helpers';
import mapContractToForm from '../../util/mapContractToForm';
import { LineItem } from '../../validationSchemas/LineItemSchema';
import DatePicker from '../forms/DatePicker';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import BreakingFlex from '../shared/BreakingFlex';
import DataGrid from '../shared/DataGrid';
import CustomDeliveryProfileModal, { CustomDeliveryProfile } from './CustomDeliveryProfileModal';

const renderDateCell = (value: any) => {
  const date = formatServerDate(value);
  return date ? format(date, 'MM/dd/yyyy') : '—';
};

interface LineItemTimingSectionProps {
  lineItemData: LineItem;
}

function LineItemTimingSection(props: LineItemTimingSectionProps) {
  const { lineItemData } = props;
  const theme = useTheme();
  const contractConfigurationContext = useContractConfigurationContext();
  const contractData = get(contractConfigurationContext, 'contractData', {});
  const contractForm = mapContractToForm(contractData);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { startDate: contractStartDate, endDate: contractEndDate } = contractForm;

  const form = useFormContext();

  const {
    watch,
    control,
    formState: { errors },
  } = form;

  const timingSectionRef = useRef<HTMLDivElement>(null);

  const rawProfileErrors = errors.customDeliveryProfiles;
  const errorRowIndices = new Set<number>();
  const errorMessages: string[] = [];

  const profileErrorEntries: any[] = [];
  if (Array.isArray(rawProfileErrors)) {
    rawProfileErrors.forEach((entry, i) => {
      profileErrorEntries[i] = entry;
    });
  } else if (rawProfileErrors && typeof rawProfileErrors === 'object') {
    Object.keys(rawProfileErrors).forEach((k) => {
      if (!Number.isNaN(Number(k))) {
        profileErrorEntries[Number(k)] = (rawProfileErrors as any)[k];
      }
    });
  }

  for (let i = 0; i < profileErrorEntries.length; i += 1) {
    if (profileErrorEntries[i]) {
      errorRowIndices.add(i);
    }
  }
  if (profileErrorEntries.some((e: any) => e?.overlapError)) {
    errorMessages.push("Delivery profiles can't have overlapping flight dates.");
  }
  if (profileErrorEntries.some((e: any) => e?.requiredFieldError)) {
    errorMessages.push('Start Date, End Date, Bonus, and Dollar Budget are required for all profiles.');
  }
  const hasProfileErrors = errorMessages.length > 0;

  useEffect(() => {
    if (hasProfileErrors && timingSectionRef.current) {
      timingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hasProfileErrors]);

  const { fields: customDeliveryProfiles, replace: replaceCustomDeliveryProfiles } = useFieldArray({
    control,
    name: 'customDeliveryProfiles',
  });

  const watchedProfiles = watch('customDeliveryProfiles');
  const hasProfiles = (watchedProfiles?.length ?? 0) > 0;

  // Derive a primitive fingerprint so the effect only fires when profile
  // dates/active flags actually change — not on every render (watch()
  // returns a new array reference each time, which previously caused an
  // infinite setValue → re-render → effect loop).
  const profilesFingerprint = (watchedProfiles || [])
    .map((p: any) => `${p.startDate?.valueOf?.() ?? p.startDate}|${p.endDate?.valueOf?.() ?? p.endDate}|${p.active}`)
    .join(',');

  useEffect(() => {
    if (!watchedProfiles || watchedProfiles.length === 0) return;
    const hasActiveProfile = watchedProfiles.some((p: any) => p.active === true);
    const startDates = watchedProfiles.map((p: any) => p.startDate).filter(Boolean);
    if (startDates.length > 0 && !hasActiveProfile) {
      const earliest = startDates.reduce((a: any, b: any) => (a < b ? a : b));
      form.setValue('startDate', earliest, { shouldDirty: true });
    }
    const endDates = watchedProfiles.map((p: any) => p.endDate).filter(Boolean);
    if (endDates.length > 0 && !hasActiveProfile) {
      const latest = endDates.reduce((a: any, b: any) => (a > b ? a : b));
      form.setValue('endDate', latest, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesFingerprint]);

  // Keep startDate / endDate watchers for the flight-date inputs at the top of the section
  const lineItemStartDate = watch('startDate');
  const lineItemEndDate = watch('endDate');

  // Resolve numeric caps from the line item (form values may be strings with commas)
  // Track the live form values, not lineItemData: raising the budget/bonus and
  // then opening flighting without saving first is exactly the case the
  // allocation warnings need to catch.
  const impressionBudgetCap = Number(removeCommas(watch('advisoryBudget') ?? 0)) || 0;
  const bonusCap = Number(removeCommas(watch('bonus') ?? 0)) || 0;

  // Build a normalized list of existing profiles to seed the modal
  const existingProfiles: CustomDeliveryProfile[] = (watchedProfiles || []).map((p: any) => ({
    id: p.id,
    profile_id: p.profile_id,
    io_id: p.io_id,
    startDate: p.startDate ? new Date(p.startDate) : null,
    endDate: p.endDate ? new Date(p.endDate) : null,
    bonus: p.bonus,
    dollarBudget: p.dollarBudget,
    deliveredImpressions: p.deliveredImpressions,
    profileImpressionBudget: p.profileImpressionBudget,
    disabled: p.disabled,
    active: p.active,
  }));

  const handleSaveProfiles = (next: CustomDeliveryProfile[]) => {
    // Track ids we removed, so the form sends them as deletions on submit.
    const previousIds = (watchedProfiles || []).map((p: any) => p.id).filter((id: any) => typeof id === 'number');
    const remainingIds = new Set(next.map((p) => p.id).filter((id) => typeof id === 'number'));
    const removedIds: number[] = previousIds.filter((id: number) => !remainingIds.has(id));

    if (removedIds.length > 0) {
      const current = (form.getValues('deletedProfileIds' as any) as number[]) || [];
      form.setValue('deletedProfileIds' as any, [...current, ...removedIds]);
    }

    // Set startDate to the earliest profile start (matches the auto-set useEffect's behavior),
    // with shouldValidate so any stale "required" error on the field clears now rather than
    // waiting for the next blur/submit. We do this here (not in the effect) so validation
    // only runs once per save — putting shouldValidate inside the watch-driven effect causes
    // a render loop.
    const profileStartDates = next.map((p) => p.startDate).filter(Boolean) as Date[];
    if (profileStartDates.length > 0 && !next.some((p) => p.active === true)) {
      const earliest = profileStartDates.reduce((a, b) => (a < b ? a : b));
      form.setValue('startDate', earliest, { shouldValidate: true, shouldDirty: true });
    }

    // Replace the entire customDeliveryProfiles array with what the modal returned.
    replaceCustomDeliveryProfiles(next as any);
  };

  // ----- Read-only grid -----
  const dataGridRows = customDeliveryProfiles.map((field: any, index) => {
    const nextField = customDeliveryProfiles[index + 1] as any;
    let hasGap = false;
    if (nextField && field.endDate && nextField.startDate) {
      const endMs = new Date(field.endDate).getTime();
      const nextStartMs = new Date(nextField.startDate).getTime();
      // Gap exists if the next profile doesn't start the day after this one ends
      hasGap = nextStartMs - endMs > 24 * 60 * 60 * 1000;
    }
    const periodLabel = `${renderDateCell(field.startDate)} – ${renderDateCell(field.endDate)}`;
    return {
      id: field.id,
      rowIndex: index,
      period: periodLabel,
      profileImpressionBudget: Number(removeCommas(field.profileImpressionBudget ?? 0)) || 0,
      bonus: Number(removeCommas(field.bonus ?? 0)) || 0,
      hasGap,
      isSubtotal: false,
    };
  });

  const totalImpressions = dataGridRows.reduce((sum, r) => sum.add(r.profileImpressionBudget), currency(0)).value;
  const totalBonus = dataGridRows.reduce((sum, r) => sum.add(r.bonus), currency(0)).value;
  const regularPct = impressionBudgetCap > 0 ? (totalImpressions / impressionBudgetCap) * 100 : 0;
  const bonusPct = bonusCap > 0 ? (totalBonus / bonusCap) * 100 : 0;

  const allocationTotals = { totalImpressions, totalBonus, impressionBudgetCap, bonusCap };
  const hasAllocationShortfall = hasProfiles && getAllocationShortfalls(allocationTotals).length > 0;

  const subtotalRow = {
    id: '__subtotal__',
    rowIndex: -1,
    period: 'Subtotal',
    profileImpressionBudget: totalImpressions,
    bonus: totalBonus,
    hasGap: false,
    isSubtotal: true,
    regularPct,
    bonusPct,
  };

  const columns: GridColDef[] = [
    {
      field: 'period',
      headerName: 'Custom Flighting Period',
      flex: 2,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <Typography sx={{ fontSize: '12px', color: theme.palette.text.primary }}>
            {params.value}
          </Typography>
          {params.row.hasGap && (
            <Tooltip title="There's a time gap between this delivery period and the next." arrow placement="top">
              <span style={{ display: 'flex', alignItems: 'center', color: theme.palette.warning.main }}>
                <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 14 }} />
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'profileImpressionBudget',
      headerName: 'Impression Budget',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ height: '100%', width: '100%' }}>
          <Typography sx={{ fontSize: '12px', color: theme.palette.text.primary }}>
            {params.row.isSubtotal && impressionBudgetCap > 0
              ? `${Number(params.value).toLocaleString('en-US')} (${params.row.regularPct.toFixed(1)}%)`
              : Number(params.value || 0).toLocaleString('en-US')}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'bonus',
      headerName: 'Bonus Impressions',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ height: '100%', width: '100%' }}>
          <Typography sx={{ fontSize: '12px', color: theme.palette.text.primary }}>
            {params.row.isSubtotal && bonusCap > 0
              ? `${Number(params.value).toLocaleString('en-US')} (${params.row.bonusPct.toFixed(1)}%)`
              : Number(params.value || 0).toLocaleString('en-US')}
          </Typography>
        </Stack>
      ),
    },
  ];

  const gridSx = {
    '& .MuiDataGrid-root': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
      fontSize: '12px',
    },
    '& .MuiDataGrid-columnHeaders': {
      bgcolor: theme.palette.grey[50],
    },
    '& .MuiDataGrid-columnHeader': {
      padding: '0 8px',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '12px',
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontSize: '12px',
      padding: '0 8px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:focus': { outline: 'none' },
      '&:focus-within': { outline: 'none' },
    },
    '& .MuiDataGrid-pinnedRows .MuiDataGrid-row': {
      bgcolor: theme.palette.grey[50],
    },
    '& .MuiDataGrid-pinnedRows': {
      boxShadow: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
      display: 'none',
    },
    '& .profile-error-row': {
      backgroundColor: 'rgba(211, 47, 47, 0.08)',
      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.15)' },
      '&.Mui-selected': {
        backgroundColor: 'rgba(211, 47, 47, 0.15)',
        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.15)' },
      },
    },
  };

  return (
    <div ref={timingSectionRef}>
      <FormSection title={lineItemFormText.sectionTitles.timing}>
        <FormSubsection>
          <BreakingFlex breakpoint="md">
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" sx={{ mt: '14px !important' }}>
                {lineItemFormText.subsectionTitles.flightDates}
              </Typography>
              <DatePicker
                label="Start Date"
                name="startDate"
                minDate={contractStartDate}
                maxDate={contractEndDate}
                disabled={hasProfiles}
              />
              <Typography variant="caption" sx={{ mt: '14px !important' }}>
                To
              </Typography>
              <DatePicker
                label="End Date"
                name="endDate"
                minDate={lineItemStartDate}
                maxDate={contractEndDate}
                disabled={hasProfiles}
              />
            </Stack>
          </BreakingFlex>
        </FormSubsection>
        <Stack spacing={2}>
          {hasProfileErrors && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {errorMessages.map((msg) => (
                <div key={msg}>{msg}</div>
              ))}
            </Alert>
          )}
          {hasProfiles && (
            <Box sx={gridSx}>
              <DataGrid
                key={customDeliveryProfiles.map((f) => f.id).join(',')}
                columns={columns}
                rows={dataGridRows}
                pinnedRows={{ bottom: [subtotalRow] }}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                disableAggregation
                disableRowGrouping
                getRowClassName={(params) =>
                  errorRowIndices.has(params.row.rowIndex as number) ? 'profile-error-row' : ''
                }
              />
            </Box>
          )}
          {hasAllocationShortfall && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {getAllocationShortfallMessage(allocationTotals)}
            </Alert>
          )}
          <Button variant="outlined" onClick={() => setIsModalOpen(true)} sx={{ alignSelf: 'flex-start' }}>
            {hasProfiles ? 'Edit Custom Flighting' : 'Add Custom Flighting'}
          </Button>
        </Stack>
      </FormSection>

      <CustomDeliveryProfileModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveProfiles}
        initialProfiles={existingProfiles}
        lineItemStartDate={lineItemStartDate ? new Date(lineItemStartDate) : null}
        lineItemEndDate={lineItemEndDate ? new Date(lineItemEndDate) : null}
        contractStartDate={contractStartDate ? new Date(contractStartDate) : null}
        contractEndDate={contractEndDate ? new Date(contractEndDate) : null}
        impressionBudgetCap={impressionBudgetCap}
        bonusCap={bonusCap}
        lineItemId={lineItemData.lineItemId ?? undefined}
      />
    </div>
  );
}

export default LineItemTimingSection;
